import { clearWellsList, loadWellsListFromDB, saveWellsListToDB, clearPlanList } from 'utils/indexedDB'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { getItemFromLS } from 'utils/localStorage'
import { tokenSearchFunction } from 'components/common/searchFunctions'
import useAxiosGzip from 'components/common/hooks/useAxiosGzip'
import useInnovaAuth from 'components/common/hooks/useInnovaAuth'
import useObjectsOfInterest from 'components/common/hooks/useObjectsOfInterest'

const useWellList = (databaseOrg) => {
  const [wellList, setWellList] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [isWellListRefreshing, setIsWellListRefreshing] = useState(false)
  const fetchWells = useAxiosGzip({ url: '/wellsGz', timeout: 180000 })
  const fetchWellNames = useAxiosGzip({ url: '/wellsNamesGz', timeout: 120000 })
  const { objectsOfInterest, isObjectOfInterest } = useObjectsOfInterest()
  const { getDatabaseOrg, getDefaultDatabaseOrg } = useInnovaAuth()

  const getFilteredWellList = ({
    searchText = '',
    activeFilters = {},
    pinOptions = [],
    onlyShowFollowing = false,
    refWell = null,
    homeOrgOnly = false,
    onlyShowPad = false,
  }) => {
    if (!Array.isArray(wellList) || wellList.length === 0) return []

    const normalize = (v) => (typeof v === 'string' ? v.trim().toUpperCase() : '')

    const rigs = new Set((objectsOfInterest?.rigs || []).map(normalize))
    const districts = new Set((objectsOfInterest?.districts || []).map(normalize))
    const operators = new Set((objectsOfInterest?.operators || []).map(normalize))

    const hasObjectsOfInterest = rigs.size > 0 || districts.size > 0 || operators.size > 0
    const safeActiveFilters = activeFilters && typeof activeFilters === 'object' ? activeFilters : {}

    const allowed = {}
    if (Array.isArray(pinOptions)) {
      for (const col of pinOptions) {
        if (col.external) continue
        allowed[col.key] = { label: col.label, values: new Set() }
      }
    }

    const homeOrg = getDatabaseOrg()

    const wellsOnPad = onlyShowPad && refWell ? getWellsOnPad(refWell) : []

    const filtered = wellList.filter((well) => {
      if (!well || typeof well !== 'object') return false
      if (onlyShowFollowing && !isObjectOfInterest(well.rig, well.operator, well.district)) return false
      if (refWell && well.actualWell === refWell.wellName && well.organization === refWell.organization) return false
      if (homeOrgOnly && well.organization !== homeOrg) return false

      if (
        onlyShowPad &&
        !wellsOnPad.some((w) => w.actualWell === well.actualWell && w.organization === well.organization)
      ) {
        return false
      }

      const status = normalize(well?.wellStatus)

      if (!status || status === 'N/A') {
        return false
      }

      if (
        typeof tokenSearchFunction === 'function' &&
        tokenSearchFunction(well, searchText, [
          'actualWell',
          'jobNum',
          'operator',
          'state',
          'county',
          'rig',
          'wellStatus',
        ]) === false
      ) {
        return false
      }

      if (Array.isArray(pinOptions)) {
        const matches = Object.entries(safeActiveFilters).every(([key, values]) => {
          if (!(values instanceof Set) || values.size === 0) {
            return true
          }

          switch (key) {
            case 'bhaTypes': {
              const wellValues = well.bhaTypes || []
              return wellValues.some((v) => values.has(normalize(v)))
            }

            case 'holeSizes': {
              const wellValues = well.holeSizes || []
              return wellValues.some((wellSize) =>
                Array.from(values).some((filterSize) => Math.abs(Number(wellSize) - Number(filterSize)) <= 0.01),
              )
            }

            default: {
              const normalizedValues = new Set(Array.from(values).map(normalize))

              const wellValue = normalize(well[key])

              return normalizedValues.has(wellValue)
            }
          }
        })

        if (!matches) return false

        for (const col of pinOptions) {
          if (col.external) continue
          const v = well[col.key]
          if (v != null && v !== '') allowed[col.key].values.add(v)
        }
      }

      return true
    })

    const groups = new Map()

    filtered.forEach((well) => {
      let groupKey = well.operator || 'Unknown'

      if (hasObjectsOfInterest) {
        const rigVal = normalize(well.rig)
        const opVal = normalize(well.operator)
        const districtVal = normalize(well.district)

        const isFavorite = operators.has(opVal) || rigs.has(rigVal) || districts.has(districtVal)

        if (isFavorite) groupKey = 'Favorite'
      }

      if (!groups.has(groupKey)) {
        groups.set(groupKey, [])
      }

      groups.get(groupKey).push(well)
    })

    const flatList = []

    // Always render Favorite group first if it exists
    if (groups.has('Favorite')) {
      flatList.push({ header: 'Favorite', operator: 'Favorite' })
      groups.get('Favorite').forEach((well) => flatList.push(well))
    }

    // Then render all other operators
    groups.forEach((wells, operator) => {
      if (operator === 'Favorite') return

      flatList.push({ header: operator, operator })
      wells.forEach((well) => flatList.push(well))
    })

    const allowedColumnValues =
      Object.keys(allowed).length > 0
        ? Object.fromEntries(Object.entries(allowed).map(([k, v]) => [k, { values: Array.from(v.values) }]))
        : undefined

    return { flatList, allowedColumnValues }
  }

  const fetchWellsList = async (updateStartTime = null, wellNames = '', checkUpdates = true) => {
    const org = databaseOrg
    let params = { getFootage: true, wellNames: wellNames || '' }
    if (!checkUpdates) {
      params = { getFootage: true, wellNames: wellNames || '', checkUpdates: false }
    }

    if (updateStartTime) params.updateStartTime = updateStartTime
    const isFull = !updateStartTime
    const response = await fetchWells(params)

    if (!Array.isArray(response?.data)) {
      if (isFull) {
        await clearWellsList()
      }
      return []
    }

    try {
      if (isFull) {
        await clearWellsList()
      }
      let currentUnits = getItemFromLS('units', 'selectedUnits')

      await saveWellsListToDB(response.data, org, currentUnits)
    } catch (err) {
      // console.warn('Failed to save wells to IndexedDB:', err)
    }

    return response.data
  }

  const refreshByWellNames = async (wellNames, checkUpdates = true, setLoading = true) => {
    if (setLoading) setIsLoading(true)
    const org = databaseOrg

    const cachedWells = await loadWellsListFromDB()

    if (!Array.isArray(cachedWells) || cachedWells.length === 0) {
      setIsLoading(false)
      return
    }

    if (cachedWells[0].selectedOrg !== org) {
      setIsLoading(false)
      return
    }

    let newWellsList = cachedWells
    setWellList(cachedWells)

    const incremental = await fetchWellsList(null, wellNames, checkUpdates)
    if (Array.isArray(incremental) && incremental.length > 0) {
      const mergedMap = new Map()
      const getKey = (w) =>
        `${w.organization?.trim()?.toUpperCase() || ''}|${w.actualWell?.trim()?.toUpperCase() || ''}`

      cachedWells.forEach((w) => mergedMap.set(getKey(w), w))
      incremental.forEach((w) => mergedMap.set(getKey(w), w))

      const merged = Array.from(mergedMap.values())

      newWellsList = merged
      setWellList(merged)
      let currentUnits = getItemFromLS('units', 'selectedUnits')
      await saveWellsListToDB(merged, org, currentUnits)
    }

    const wellNamesResponse = await fetchWellNames()
    const validKeys = Array.isArray(wellNamesResponse?.data)
      ? wellNamesResponse.data.map(
          (w) => `${w.organization?.trim()?.toUpperCase() || ''}|${w.actualWell?.trim()?.toUpperCase() || ''}`,
        )
      : []

    if (validKeys.length > 0 && Array.isArray(newWellsList)) {
      const getKey = (w) =>
        `${w.organization?.trim()?.toUpperCase() || ''}|${w.actualWell?.trim()?.toUpperCase() || ''}`
      const filtered = newWellsList.filter((w) => validKeys.includes(getKey(w)))

      const deletedCount = newWellsList.length - filtered.length
      if (deletedCount > 0) {
        newWellsList = filtered
        setWellList(filtered)

        await clearWellsList()
        let currentUnits = getItemFromLS('units', 'selectedUnits')

        await saveWellsListToDB(filtered, org, currentUnits)
      }

      setIsLoading(false)
    }
  }

  const refresh = async () => {
    setIsLoading(true)
    await clearWellsList()
    const data = await fetchWellsList(null)
    setWellList(data)
    setIsLoading(false)
  }

  const refreshLast48Hours = async () => {
    setIsLoading(true)
    const now = new Date()
    const past48Hours = new Date(now.getTime() - 48 * 60 * 60 * 1000).toISOString()
    const org = databaseOrg

    const cachedWells = await loadWellsListFromDB()

    if (!Array.isArray(cachedWells) || cachedWells.length === 0) {
      setIsLoading(false)
      return
    }

    // should we even allow this?
    if (cachedWells[0].selectedOrg !== org) {
      setIsLoading(true)
      const fresh = await fetchWellsList(null)
      setIsLoading(false)
      setWellList(fresh)
      return
    }
    ////////////////////////////

    let newWellsList = cachedWells
    setWellList(cachedWells)

    const incremental = await fetchWellsList(past48Hours)
    if (Array.isArray(incremental) && incremental.length > 0) {
      const mergedMap = new Map()
      const getKey = (w) =>
        `${w.organization?.trim()?.toUpperCase() || ''}|${w.actualWell?.trim()?.toUpperCase() || ''}`

      cachedWells.forEach((w) => mergedMap.set(getKey(w), w))
      incremental.forEach((w) => mergedMap.set(getKey(w), w))

      const merged = Array.from(mergedMap.values())

      newWellsList = merged
      setWellList(merged)
      let currentUnits = getItemFromLS('units', 'selectedUnits')
      await saveWellsListToDB(merged, org, currentUnits)
    }

    const wellNamesResponse = await fetchWellNames()
    const validKeys = Array.isArray(wellNamesResponse?.data)
      ? wellNamesResponse.data.map(
          (w) => `${w.organization?.trim()?.toUpperCase() || ''}|${w.actualWell?.trim()?.toUpperCase() || ''}`,
        )
      : []

    if (validKeys.length > 0 && Array.isArray(newWellsList)) {
      const getKey = (w) =>
        `${w.organization?.trim()?.toUpperCase() || ''}|${w.actualWell?.trim()?.toUpperCase() || ''}`
      const filtered = newWellsList.filter((w) => validKeys.includes(getKey(w)))

      const deletedCount = newWellsList.length - filtered.length
      if (deletedCount > 0) {
        newWellsList = filtered
        setWellList(filtered)

        await clearWellsList()
        let currentUnits = getItemFromLS('units', 'selectedUnits')

        await saveWellsListToDB(filtered, org, currentUnits)
      }
    }

    setIsLoading(false)
  }

  // moved function here to trigger on a timer
  const initFromIndexedDB = async () => {
    setIsWellListRefreshing(true)
    const org = databaseOrg
    const units = getItemFromLS('units', 'selectedUnits')

    try {
      const cachedWells = await loadWellsListFromDB()

      if (Array.isArray(cachedWells) && cachedWells.length > 0) {
        if (cachedWells[0].selectedOrg !== org || cachedWells[0].selectedUnits !== units) {
          setIsLoading(true)
          await clearPlanList()
          await clearWellsList()
          const fresh = await fetchWellsList(null)
          setIsLoading(false)
          setWellList(fresh)
          return
        }

        const cachedTimes = cachedWells
          .map((w) => w.lastUpdated) //changing cachedAt to lastUpdated. This will check against the last time a well actually received an update rather than the last time it was pulled from the cache.
          .filter(Boolean)
          .map((t) => new Date(t).getTime())

        const lastStoredTime = cachedTimes.length ? new Date(Math.max(...cachedTimes)) : null

        let updateStartTime = null
        if (lastStoredTime) {
          const fiveMinAgo = new Date(lastStoredTime.getTime() - 5 * 60 * 1000)
          updateStartTime = fiveMinAgo.toISOString()
        }

        let newWellsList = cachedWells
        setWellList(cachedWells)

        const incremental = await fetchWellsList(updateStartTime)
        if (Array.isArray(incremental) && incremental.length > 0) {
          const mergedMap = new Map()
          const getKey = (w) =>
            `${w.organization?.trim()?.toUpperCase() || ''}|${w.actualWell?.trim()?.toUpperCase() || ''}`

          cachedWells.forEach((w) => mergedMap.set(getKey(w), w))
          incremental.forEach((w) => mergedMap.set(getKey(w), w))

          const merged = Array.from(mergedMap.values())

          newWellsList = merged
          setWellList(merged)
          let currentUnits = getItemFromLS('units', 'selectedUnits')
          await saveWellsListToDB(merged, org, currentUnits)
        }

        const wellNamesResponse = await fetchWellNames()
        const validKeys = Array.isArray(wellNamesResponse?.data)
          ? wellNamesResponse.data.map(
              (w) => `${w.organization?.trim()?.toUpperCase() || ''}|${w.actualWell?.trim()?.toUpperCase() || ''}`,
            )
          : []

        if (validKeys.length > 0 && Array.isArray(newWellsList)) {
          const getKey = (w) =>
            `${w.organization?.trim()?.toUpperCase() || ''}|${w.actualWell?.trim()?.toUpperCase() || ''}`
          const filtered = newWellsList.filter((w) => validKeys.includes(getKey(w)))

          const deletedCount = newWellsList.length - filtered.length
          if (deletedCount > 0) {
            newWellsList = filtered
            setWellList(filtered)

            await clearWellsList()
            let currentUnits = getItemFromLS('units', 'selectedUnits')

            await saveWellsListToDB(filtered, org, currentUnits)
          }
        }
      } else {
        setIsLoading(true)
        const fresh = await fetchWellsList(null)
        setWellList(fresh)
      }
    } catch (err) {
      console.warn('Failed to load wells from IndexedDB:', err)
      setIsLoading(true)
      const fresh = await fetchWellsList(null)
      setWellList(fresh)
    } finally {
      setIsLoading(false)
      setIsWellListRefreshing(false)
    }
  }

  useEffect(() => {
    if (!databaseOrg) return

    initFromIndexedDB()

    const intervalId = setInterval(
      () => {
        initFromIndexedDB()
      },
      30 * 60 * 1000,
    ) // Refresh every 30 minutes

    return () => clearInterval(intervalId)
  }, [databaseOrg]) // eslint-disable-line react-hooks/exhaustive-deps

  const setWellListItem = async (well) => {
    if (!well || !well.actualWell || !well.organization) return

    const org = databaseOrg
    setWellList((prev) => {
      const updated = prev.map((w) =>
        w.actualWell === well.actualWell && w.organization === well.organization ? { ...w, ...well } : w,
      )
      let currentUnits = getItemFromLS('units', 'selectedUnits')

      saveWellsListToDB(
        updated.filter((w) => w.actualWell === well.actualWell && w.organization === well.organization),
        org,
        currentUnits,
      )
      return updated
    })
  }

  const getWell = useCallback(
    ({ actualWell, organization }) => {
      if (!actualWell) return null
      if (!Array.isArray(wellList) || wellList.length === 0) return null
      return wellList.find((w) => w.actualWell === actualWell && w.organization === organization)
    },
    [wellList],
  )

  const getDetailsForWell = ({ actualWell, organization }) => {
    if (!actualWell) return null
    if (!Array.isArray(wellList) || wellList.length === 0) return null
    const well = wellList.find((w) => w.actualWell === actualWell && w.organization === organization)

    let bhaDetails = Array.isArray(well?.bhaDetails) ? well?.bhaDetails : []

    bhaDetails = bhaDetails.map((bha) => {
      if (!bha?.data) return bha
      try {
        const parsedData = JSON.parse(bha.data)
        const label = `${parsedData.bhaNumRep}: ${parsedData.bhaDesc}`
        return { ...parsedData, ...bha, label }
      } catch {
        return { ...bha, label: bha.key }
      }
    })

    let phaseDetails = Array.isArray(well?.phaseDetails) ? well?.phaseDetails : []
    let formationDetails = Array.isArray(well?.formationDetails) ? well?.formationDetails : []
    let holeSizeDetails = Array.isArray(well?.holeSizeDetails) ? well?.holeSizeDetails : []

    return {
      ...well,
      bhaDetails: bhaDetails.sort((a, b) => a.label.localeCompare(b.label)),
      phaseDetails: phaseDetails.sort((a, b) => a.key.localeCompare(b.key)),
      formationDetails: formationDetails.sort((a, b) => a.key.localeCompare(b.key)),
      holeSizeDetails: holeSizeDetails.sort((a, b) => a.key.localeCompare(b.key)),
    }
  }

  const compareRigEndDate = (rigA, rigB) => {
    const endDateA = rigA.endDate ? new Date(rigA.endDate) : new Date('1900-01-01')
    const endDateB = rigB.endDate ? new Date(rigB.endDate) : new Date('1900-01-01')

    return endDateA > endDateB ? rigA : rigB
  }

  const getRigs = useCallback((wellList) => {
    if (!wellList) return []
    const rigMap = {}

    wellList.forEach((well) => {
      if (!well.rig) return
      if (well.rig === '<nil>' || well.rig === 'nil' || well.rig.trim() === '') return
      if (well.wellStatus !== 'Active') return

      if (!rigMap[well.organization]) {
        rigMap[well.organization] = {}
      }

      if (!rigMap[well.organization][well.rig]) {
        rigMap[well.organization][well.rig] = well
        return
      }

      rigMap[well.organization][well.rig] = compareRigEndDate(rigMap[well.organization][well.rig], well)
    })

    return Object.values(rigMap).flatMap((orgRigs) => Object.values(orgRigs))
  }, [])

  const hasMultiDbAccess = useMemo(() => {
    const homeOrg = getDefaultDatabaseOrg()

    for (const well of wellList) {
      if (well.organization !== homeOrg) {
        return true
      }
    }
    return false
  }, [wellList, getDefaultDatabaseOrg])

  const getNonHomeOrgWells = useCallback(() => {
    const homeOrg = getDefaultDatabaseOrg()
    return wellList.filter(
      (well) => well.organization !== homeOrg && well.wellStatus !== '' && well.wellStatus !== 'N/A',
    )
  }, [wellList, getDefaultDatabaseOrg])

  const filterDropDowns = useMemo(() => {
    let filterValues = {
      wellStatuses: Array.from(new Set(wellList.map((w) => w.wellStatus).filter(Boolean))).sort(),
      formations: Array.from(new Set(wellList.map((w) => w.formation).filter(Boolean))).sort(),
      rigs: Array.from(new Set(wellList.map((w) => w.rig).filter(Boolean))).sort(),
      facilities: Array.from(new Set(wellList.map((w) => w.facility).filter(Boolean))).sort(),
      bhaTypes: Array.from(new Set(wellList.flatMap((w) => w.bhaTypes || []).filter(Boolean))).sort(),
      holeSizes: Array.from(
        new Set(
          wellList
            .flatMap((w) => w.holeSizes || [])
            .filter(Boolean)
            .map((v) => Number(v).toFixed(3)),
        ),
      ).sort((a, b) => Number(a) - Number(b)),
    }

    return filterValues
  }, [wellList])

  const getWellsOnPad = useCallback(
    (refWell) => {
      if (!refWell) return []

      const refWellRec = wellList.find(
        (well) => well.actualWell === refWell.wellName && well.organization === refWell.organization,
      )

      if (!refWellRec) return []

      const filtered = wellList.filter((well) => {
        if (!well || typeof well !== 'object') return false
        if (well.actualWell === refWellRec.actualWell && well.organization === refWellRec.organization) return false
        if (well.facility !== refWellRec.facility) return false
        if (well.wellStatus === 'N/A' || well.wellStatus === '') return false
        if (well.organization !== refWellRec.organization) return false

        return true
      })

      return filtered
    },
    [wellList],
  )

  const getWellsForRig = useCallback(
    (refWell, numWells = 10) => {
      if (!refWell) return []

      const refWellRec = wellList.find(
        (well) => well.actualWell === refWell.wellName && well.organization === refWell.organization,
      )
      if (!refWellRec) return []

      const filtered = wellList.filter((well) => {
        if (!well || typeof well !== 'object') return false
        if (well.actualWell === refWellRec.actualWell && well.organization === refWellRec.organization) return false
        if (well.rig !== refWellRec.rig) return false
        if (well.wellStatus === 'N/A' || well.wellStatus === '') return false
        if (well.organization !== refWellRec.organization) return false

        return true
      })

      filtered.sort((a, b) => {
        const aEnd = a.endDate ? new Date(a.endDate) : new Date(0)
        const bEnd = b.endDate ? new Date(b.endDate) : new Date(0)
        return bEnd - aEnd
      })

      return filtered.slice(0, numWells)
    },
    [wellList],
  )

  const getRecentRigWells = useCallback(
    (numWells = 10) => {
      if (!Number.isInteger(numWells) || numWells <= 0) {
        return {
          error: 'Invalid number of wells requested',
          data: [],
        }
      }

      const rigs = getRigs(wellList)

      if (rigs.length === 0) {
        return {
          error: 'No rigs found',
          data: [],
        }
      }

      if (rigs.length > numWells) {
        return {
          error: `Too many rigs. Only ${numWells} wells can be selected`,
          data: [],
        }
      }

      const wellsPerRig = Math.floor(numWells / rigs.length)
      const remainder = numWells % rigs.length
      const recentWells = []

      for (let i = 0; i < rigs.length; i++) {
        const rigWell = rigs[i]
        const numberToSelect = wellsPerRig + (i === 0 ? remainder : 0)
        if (numberToSelect === 1) {
          recentWells.push(rigWell)
          continue
        }

        const wells = getWellsForRig(
          {
            wellName: rigWell.actualWell,
            organization: rigWell.organization,
          },
          numberToSelect - 1,
        )

        recentWells.push(rigWell, ...wells) // getWellsForRig doesn't return the original rigWell, adding manually
      }

      return {
        error: null,
        data: recentWells,
      }
    },
    [wellList, getRigs, getWellsForRig],
  )

  const getMaxDepths = useCallback(
    (wellName, organization) => {
      const well = wellList?.find((w) => w.actualWell === wellName && w.organization === organization)
    
      return {
        maxDepthPlan: well?.maxDepthPrincipalPlan || 0,
        maxDepth: parseFloat(well?.currentDepth) || 0,
      }
    },
    [wellList],
  )

  return {
    wellList,
    setWellListItem,
    getWell,
    getDetailsForWell,
    refresh,
    refreshLast48Hours,
    refreshByWellNames,
    isLoading,
    getFilteredWellList,
    getRigs,
    hasMultiDbAccess,
    getNonHomeOrgWells,
    filterDropDowns,
    getWellsOnPad,
    getWellsForRig,
    getRecentRigWells,
    isWellListRefreshing,
    getMaxDepths,
  }
}

export default useWellList
